import { createSelector } from 'reselect'
import { orderBy } from 'lodash'
import { DateTime } from 'luxon'

import { inOperator, ReduxState } from '@/utils/typeHelpers'
import { History } from './histories.slice'
import { selectActiveQuarterlyTaxEstimateDetails } from '../quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'

const selectAllHistories = (state: ReduxState) => state.admin.allHistories

const getAllHistoriesSortedByCreationDesc = createSelector(
  selectAllHistories,
  (histories) =>
    orderBy(Object.values(histories), [(obj) => obj.createdAt], ['desc'])
)

// Returns all Histories whose modelName matches the argument.
// Accepts a filter function to return a subset of results, e.g. filter for specific userId on the models
export const getHistoriesForModelRecentFirstSelector = createSelector(
  [
    getAllHistoriesSortedByCreationDesc,
    (
      _: unknown,
      args: { modelName: string; filterFn: (_history: History) => boolean }
    ) => args,
  ],
  (histories: History[], args) =>
    histories.filter((h) => h.modelName === args.modelName && args.filterFn(h))
)

// todo this selector is written incorrectly - should not have selector inside of it
export const getFinancialProfileHistoriesSortedSelector = createSelector(
  [(state: ReduxState) => state, (_: unknown, userId: number) => userId],
  (state, userId) =>
    getHistoriesForModelRecentFirstSelector(state, {
      modelName: 'FinancialProfile',
      filterFn: (history: History) => {
        if (inOperator('userId', history.newVersion)) {
          return history.newVersion['userId'] === userId
        }

        return false
      },
    })
)

export const getAllFinancialProfileHistories = createSelector(
  [getAllHistoriesSortedByCreationDesc],
  (histories) => histories.filter((h) => h.modelName === 'FinancialProfile')
)

const getHistoriesForYear = (
  histories: History[],
  year: string | undefined
) => {
  const yearNumber = Number(year)
  // On a cold page load the active quarter details haven't been fetched yet,
  // so year is undefined — Luxon throws on NaN, crashing any component whose
  // selector chain reaches this before the store is warm.
  if (!year || Number.isNaN(yearNumber)) {
    return []
  }
  const startOfYear = DateTime.fromObject({ year: yearNumber }).startOf('year')
  const endOfYear = DateTime.fromObject({ year: yearNumber }).endOf('year')

  return histories.filter((history) => {
    const createdAt = DateTime.fromISO(history.createdAt)
    return createdAt >= startOfYear && createdAt <= endOfYear
  })
}

export const getFinancialProfileHistoriesForYear = createSelector(
  [getAllFinancialProfileHistories, (_: unknown, year?: string) => year],
  getHistoriesForYear
)

export const selectFPHistoriesForCurrentYear = createSelector(
  getAllFinancialProfileHistories,
  selectActiveQuarterlyTaxEstimateDetails,
  (histories, details) => getHistoriesForYear(histories, details?.taxYear)
)
